import { useState, useCallback, useRef } from "react";
import { URL_CONSTANTS } from "../../axios/urlLinksThemes";
import { useDispatch } from "react-redux";
import useGetToken from "../useGetToken";
import { processColor } from "../../shared/processColor";
import { CSS_VARIABLES } from "../../shared/cssVariable";
import { setBussinessData } from "../../store/slices/bussiness-data-slice";
import {
  IALLPRODUCTS,
  IBRANDLOGO,
  ICATEGORY,
  IHOMEPAGEData,
  IHomePageSlider,
  IRESAllData,
} from "../../shared/commonUtils";
import { httpPost, httpPostWithAuth } from "../../axios/axiosUtils";
import {
  TOKEN_EXPIRIES_MESSAGE,
  UpdateBussinessInfo,
  getBussinessData,
  getUserPrefersLang,
  setBussinessCountry,
  saveUserPrefersLang,
  makeRtlCss,
  urlParams,
} from "../../shared/helpersMethod";
import { increaseCounter, resetCounter } from "../../store/slices/apiCallCount";
import {
  setBussinessApiLoading,
  setBussinessApiSuccess,
} from "../../store/slices/progressState";

const useGetHomePage = () => {
  const [loading, setLoading] = useState(false);
  const { getToken } = useGetToken();
  const [homePageSlider, setHomePageSider] = useState<IHomePageSlider[]>([]);
  const [homePageBrandLogo, setHomeBrandLogo] = useState<IBRANDLOGO[]>([]);
  const dispatch = useDispatch();
  const userLanguage = getUserPrefersLang();
  const queryParamLang = `?lang=${userLanguage}`;
  const [homePageCategories, setHomePageCategories] = useState<ICATEGORY[]>([]);
  const [homePageProducts, setHomePageProducts] = useState<IALLPRODUCTS[]>([]);
  const [homePageAllData, setHomePageAllData] = useState<IHOMEPAGEData>();
  const [homePageBanner, setHomePageBanner] = useState();
  const curretnLang = getUserPrefersLang();

  // Prevent multiple simultaneous API calls
  const apiCallInProgress = useRef(false);
  const dataFetched = useRef(false);

  // Helper function to safely check if businessRedisData exists
  const getBusinessRedisData = () => {
    try {
      if (
        typeof window !== "undefined" &&
        window.businessRedisData &&
        window.businessRedisData !== null &&
        window.businessRedisData !== undefined &&
        window.businessRedisData !== "__BUSINESS_DATA__" &&
        typeof window.businessRedisData === "object"
      ) {
        return window.businessRedisData;
      }
    } catch (error) {
      console.warn("Error accessing businessRedisData:", error);
    }
    return null;
  };

  const bussinessVariablesHandler = useCallback(
    (homePageAllData: IHOMEPAGEData) => {
      if (!homePageAllData?.site_settings) return;

      try {
        const siteSettings = homePageAllData.site_settings;
        const processColorData = processColor(siteSettings.buttonColor);

        // Apply CSS variables
        const cssVariables = [
          [
            CSS_VARIABLES.singleBannerColorLeft,
            siteSettings.siteFeaturedProductPrimaryBackgroundColor,
          ],
          [
            CSS_VARIABLES.singleBannerColorRight,
            siteSettings.siteFeaturedProductSecondaryBackgroundColor,
          ],
          [CSS_VARIABLES.mouseHoverColor, siteSettings.mouseOverColor],
          [CSS_VARIABLES.mouseSelectionColor, siteSettings.mouseSelectionColor],
          [CSS_VARIABLES.sliderBgColor, siteSettings.sliderBackgroundColor],
          [CSS_VARIABLES.sliderTextColor, siteSettings.sliderTextColor],
          [CSS_VARIABLES.buttonColor, siteSettings.buttonColor],
          [CSS_VARIABLES.featuredProductColor, siteSettings.productTextColor],
          [
            CSS_VARIABLES.categorySliderBtnColorActive,
            siteSettings.categorySliderBtnColorActive,
          ],
          [CSS_VARIABLES.footerBgColor, siteSettings.footerBackgroundColor],
          [CSS_VARIABLES.footerTextColor, siteSettings.footerTextColor],
          [
            CSS_VARIABLES.homePageInfoBg,
            siteSettings.homepageIfnoBackgroundColor,
          ],
          [
            CSS_VARIABLES.productTabColor,
            siteSettings.productCarousalBackgroundColor,
          ],
          [CSS_VARIABLES.prdtPriceOld, siteSettings.priceSecondaryColor],
          [CSS_VARIABLES.mainPriceColor, siteSettings.priceMainColor],
          [CSS_VARIABLES.themeColorLight, processColorData.backgroundColor],
          [CSS_VARIABLES.themeBorder, processColorData.borderTopColor],
          [
            CSS_VARIABLES.brandBackgroundColor,
            siteSettings.brandBackgroundColor,
          ],
          [
            CSS_VARIABLES.hpPrdtName,
            siteSettings.productNameColor || siteSettings.buttonColor,
          ],
          [
            CSS_VARIABLES.hpPrdtBgBtn,
            siteSettings.addToCartButton || siteSettings.buttonColor,
          ],
          [
            CSS_VARIABLES.hpPrdtBtnTxt,
            siteSettings.addToCartButtonText || siteSettings.buttonColor,
          ],
          [
            CSS_VARIABLES.hpPrdtStrokeClr,
            siteSettings.productStroke || siteSettings.buttonColor,
          ],
        ];

        cssVariables.forEach(([variable, value]) => {
          if (value) {
            document.documentElement.style.setProperty(variable, value);
          }
        });

        // Handle language setting
        if (curretnLang === null && homePageAllData.defaultLang) {
          let newLang = homePageAllData.defaultLang;
          const paramLang = urlParams.get("lang");
          if (paramLang) {
            newLang = paramLang;
          }
          saveUserPrefersLang(newLang);
          if (newLang === "ar") {
            makeRtlCss();
          }
        }
      } catch (error) {
        console.error("Error in bussinessVariablesHandler:", error);
      }
    },
    [curretnLang]
  );

  const getHomePageAllData = useCallback(
    async (retryAfterTokenRefresh = false): Promise<void> => {
      // Prevent multiple simultaneous calls (but allow retry after token refresh)
      if (apiCallInProgress.current && !retryAfterTokenRefresh) {
        console.log("API call already in progress, skipping");
        return;
      }

      // Skip Redis data if this is a retry after token refresh
      if (!retryAfterTokenRefresh) {
        // Check for cached Redis data first
        const businessRedisData = getBusinessRedisData();
        if (businessRedisData && !dataFetched.current) {
          console.log("Using cached Redis data");
          try {
            setHomePageAllData(businessRedisData);
            bussinessVariablesHandler(businessRedisData);
            setBussinessCountry(
              businessRedisData.businessCountry?.country_name || ""
            );
            dispatch(setBussinessData(businessRedisData));
            UpdateBussinessInfo(businessRedisData);
            dataFetched.current = true;
            return;
          } catch (error) {
            console.error("Error processing Redis data:", error);
            // Continue to API call if Redis data processing fails
          }
        }
      }

      // Get business token
      let bussinessToken = getBussinessData();
      if (!bussinessToken?.accessToken) {
        console.log("No business token found, attempting to get token");
        try {
          const newToken = await getToken();
          // Update business token after getting new token
          bussinessToken = getBussinessData();
          if (!bussinessToken?.accessToken) {
            console.error("Still no token after getToken call");
            return;
          }
        } catch (error) {
          console.error("Failed to get token:", error);
          return;
        }
      }

      apiCallInProgress.current = true;
      setLoading(true);
      dispatch(setBussinessApiLoading());

      try {
        console.log("🚀 Making API call to get homepage data");
        const res: IRESAllData = await httpPostWithAuth(
          URL_CONSTANTS.get("URL_Get_HomePageAllData")! +
            `${userLanguage !== null ? queryParamLang : ""}`,
          {},
          bussinessToken.accessToken
        );

        if (res.type === "error" && res?.token === "expired") {
          console.log("🔄 Token expired, refreshing token and retrying");
          try {
            // Reset the API call flag to allow retry
            apiCallInProgress.current = false;

            const newToken = await getToken(true); // Force token refresh
            console.log(
              "✅ New token obtained:",
              newToken ? "Success" : "Failed"
            );

            // Retry the API call with new token
            if (newToken) {
              console.log("🔄 Retrying homepage data API call with new token");
              return getHomePageAllData(true); // Pass true to indicate this is a retry
            } else {
              throw new Error("Failed to get new token");
            }
          } catch (tokenError) {
            console.error("❌ Failed to refresh token:", tokenError);
            throw tokenError;
          }
        }

        if (res.data && res.type !== "error") {
          console.log("✅ Successfully fetched homepage data");
          setHomePageAllData(res.data);
          bussinessVariablesHandler(res.data);
          setBussinessCountry(res.data.businessCountry?.country_name || "");
          dispatch(setBussinessData(res.data));
          UpdateBussinessInfo(res.data);
          dataFetched.current = true;
        } else {
          console.error("❌ API returned error:", res);
        }
      } catch (error: any) {
        console.error("❌ API call failed:", error);
      } finally {
        setLoading(false);
        dispatch(setBussinessApiSuccess());
        apiCallInProgress.current = false;
      }
    },
    [
      getToken,
      userLanguage,
      queryParamLang,
      bussinessVariablesHandler,
      dispatch,
    ]
  );

  // Other methods remain similar but with better error handling
  const getHomePageSlider = async (
    theme: string,
    template: string,
    page: string,
    element: string
  ) => {
    setLoading(true);
    try {
      const res = await httpPostWithAuth(
        URL_CONSTANTS.get("URL_Get_Home")!,
        { theme, template, page, element },
        ""
      );
      setHomePageSider(res || []);
    } catch (error: any) {
      console.error("Failed to get homepage slider:", error.message);
      setHomePageSider([]);
    } finally {
      setLoading(false);
    }
  };

  const getHomePageBrands = async (
    theme: string,
    template: string,
    page: string,
    element: string
  ) => {
    setLoading(true);
    try {
      const res = await httpPostWithAuth(
        URL_CONSTANTS.get("URL_Get_Home")!,
        { theme, template, page, element },
        ""
      );
      setHomeBrandLogo(res || []);
    } catch (error: any) {
      console.error("Failed to get homepage brands:", error.message);
      setHomeBrandLogo([]);
    } finally {
      setLoading(false);
    }
  };

  const getHomePageBanners = async () => {
    const bussinessToken = getBussinessData();
    if (!bussinessToken?.accessToken) {
      console.log("No token available for banners");
      return;
    }

    setLoading(true);
    try {
      const res = await httpPost(
        URL_CONSTANTS.get("URL_Get_BANNERS")!,
        {},
        undefined,
        bussinessToken.accessToken
      );
      if (res?.type === "success") {
        setHomePageBanner(res.data);
      }
    } catch (error: any) {
      console.error("Failed to get homepage banners:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const getHomePageCategory = async (
    theme: string,
    template: string,
    page: string,
    element: string
  ) => {
    setLoading(true);
    try {
      const res = await httpPostWithAuth(
        URL_CONSTANTS.get("URL_Get_Home")!,
        { theme, template, page, element },
        ""
      );
      setHomePageCategories(res || []);
    } catch (error: any) {
      console.error("Failed to get homepage categories:", error.message);
      setHomePageCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const getHomePageProducts = async () => {
    const bussinessToken = getBussinessData();
    if (!bussinessToken?.accessToken) {
      console.log("No token available for products");
      return;
    }

    setLoading(true);
    try {
      const res = await httpPostWithAuth(
        URL_CONSTANTS.get("URL_Get_Products")! +
          `${userLanguage !== null ? queryParamLang : ""}`,
        {},
        bussinessToken.accessToken
      );

      if (res?.token === "expired") {
        console.log("Token expired while fetching products");
        try {
          await getToken(true);
          return getHomePageProducts(); // Retry
        } catch (tokenError) {
          console.error("Failed to refresh token for products:", tokenError);
        }
      } else if (res?.data) {
        setHomePageProducts(res.data);
      }
    } catch (error: any) {
      console.error("Failed to get homepage products:", error.message);
      setHomePageProducts([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    getHomePageCategory,
    homePageCategories,
    homePageProducts,
    homePageBanner,
    getHomePageBanners,
    homePageSlider,
    getHomePageProducts,
    homePageAllData,
    getHomePageAllData,
    homePageBrandLogo,
    getHomePageSlider,
    getHomePageBrands,
    loading,
  };
};

export default useGetHomePage;
